import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import {
    AdmissionDocGroupDto,
    AwvPreventativeDto,
    CcdaDocumentListItemDto,
    CensusBadgeDto,
    CensusDto,
    CensusFiltersDto,
    CensusGroupDto,
    CptCodeDto,
    ExportInfoDto,
    FacilityQuickNotesDto,
    FacilityStagedNotesDto,
    HospiceIntakeDto,
    ImmunizationRefusalReasonDto,
    ImmunizationRegistryCodeTablesDto,
    ImmunizationRouteCodeDto,
    MatrixCarePatientDataDto,
    MedicalSummaryDto,
    MedicalSummaryLockStatusDto,
    NoteCensusDto,
    NoteDto,
    NoteTypes,
    PatientAdtDto,
    PatientAllergyDto,
    PatientCareGoalDto,
    PatientCareTeamMemberDto,
    PatientDto,
    PatientHealthConcernDto,
    PatientImmunizationDto,
    PatientIntakeDto,
    PatientIntakeSpecialtyGroupedDto,
    PatientLabDto,
    PatientLabRecentCategoryDto,
    PatientLabRecentItemDto,
    PatientMedicationDto,
    PatientNoteDto,
    PatientProviderSupplierDto,
    PatientSearchResultsDto,
    PccPatientDataDto,
    PccProgressNoteDetailAggregationDto,
    QuickNoteDto,
    RecentSearchSelectedPatientIntakeDto,
    RegistryStaffDto,
    RequiredVisitResultDto,
    ReturnToHospitalSummaryDto,
    ScheduleDto,
    StagedNoteDto,
    VisitDto,
} from '@medone/medonehp-api-client';

export interface CqmGapHintDto {
    missingQdmElement: string;
    uiSection: string;
}

export interface CqmStatusRowDto {
    measureId: string;
    measureName: string;
    // Multi-rate measures return one row per rate, all sharing measureId/measureName.
    // populationSetKey disambiguates them; rateLabel is the human rate name (null for single-rate).
    populationSetKey?: string;
    rateLabel?: string | null;
    status: string;
    capturedSummary: string;
    sourceSection: string;
    gapHints: CqmGapHintDto[];
    lastComputedAt?: string;
}

export const visitDuesAdapter = createEntityAdapter<RequiredVisitResultDto, number>({
    selectId: (item) => item.patientIntakeId,
});

export const censusBadgesAdapter = createEntityAdapter<CensusBadgeDto, number>({
    selectId: (item) => item.patientIntakeId,
});

export const specialtiesAdapter = createEntityAdapter<PatientIntakeSpecialtyGroupedDto, number>({
    selectId: (item) => item.patientIntakeId,
});

export interface NoteFaxDict {
    noteId: number;
    faxes: ExportInfoDto[];
}

export interface CensusField {
    patientId: number;
    facilityId: number;
    field: string;
    fieldValue: boolean;
}

export interface VisitButtonDict {
    patientIntakeId: number;
    isHistoryAndPhysicalButtonVisible: boolean;
    isWoundButtonVisible: boolean;
    isInitialPMRButtonVisible: boolean;
    userIsPMRProvider: boolean;
    userIsPhysicianScribe: boolean;
    isAwvButtonVisible: boolean;
    isProgressButtonVisible: boolean;
    isDischargeButtonVisible: boolean;
}

export interface MatrixCarePatientLoadingState {
    patientIntakeId: number;
    loading: boolean;
}

export interface PccPatientLoadingState {
    patientIntakeId: number;
    loading: boolean;
}

export interface CensusState {
    errorMessage: string;
    badges: EntityState<CensusBadgeDto, number>;
    visitDues: EntityState<RequiredVisitResultDto, number>;
    specialties: EntityState<PatientIntakeSpecialtyGroupedDto, number>;
    census: CensusGroupDto[];
    censusFilters: CensusFiltersDto;
    censusGroupsToRefresh: CensusGroupDto[];
    currentPatientIntake: PatientIntakeDto;
    currentPatientIntakeLockStatus: MedicalSummaryLockStatusDto;
    currentPatientIntakeIsLocked: boolean;
    currentPatientIntakeLockActive: boolean;
    currentStagedNote: StagedNoteDto;
    currentStagedNotes: StagedNoteDto[];
    currentStagedNotesGrouped: FacilityStagedNotesDto[];
    completedNotes: NoteDto[];
    incompleteNotes: NoteDto[];
    facilityQuickNotes: FacilityQuickNotesDto;
    quickNotes: QuickNoteDto[];
    currentNote: NoteDto;
    latestHgbA1cLab: PatientLabDto;
    latestTshLab: PatientLabDto;
    pendingMedicalSummaryChanges: boolean;
    pendingMedicalSummaryFields: MedicalSummaryDto;
    matrixCarePatient: MatrixCarePatientDataDto;
    matrixCarePatientLoading: MatrixCarePatientLoadingState;
    pccPatient: PccPatientDataDto;
    pccPatientLoading: PccPatientLoadingState;
    facilityAdtsLoading: boolean;
    facilityAdts: PatientAdtDto[];
    facilityRecentAdmissions: PatientAdtDto[];
    facilityRecentDischarges: PatientAdtDto[];
    patientNoteLoading: boolean;
    patientNoteProcessing: boolean;
    patientNotes: PatientNoteDto[];
    currentPatientNote: PatientNoteDto;
    patient: PatientDto;
    searchResults: PatientSearchResultsDto;
    recentSearchSelectedPatientIntakes: RecentSearchSelectedPatientIntakeDto[];
    patientSearchLoading: boolean;
    patientSearchVisible: boolean;
    patientLabProcessing: boolean;
    dischargeModalVisible: boolean;
    overrideDischargeConfirmationVisible: boolean;
    requiresReload: boolean;
    noteIsReadonly: boolean;
    currentCensus: CensusDto;
    noteError: string;
    currentNoteType: NoteTypes;
    patientDrawerOpen: boolean;
    noteCensus: NoteCensusDto;
    notePrintContent?: string[];
    quickNotePrintContent?: QuickNoteDto[];
    sideNotePrintContent?: string;
    cptCodes: CptCodeDto[];
    procedureCptCodes: CptCodeDto[];
    noteFaxes: NoteFaxDict[];
    recentLabsLoading: boolean;
    recentLabs: PatientLabRecentCategoryDto[];
    recentLabTestDetails: PatientLabRecentItemDto[];
    schedules: ScheduleDto[];
    visits: VisitDto[];
    visitButtons: VisitButtonDict[];
    patientDocumentGroups: AdmissionDocGroupDto[];
    isQuickNoteVisible: number;
    patientIntakes: PatientIntakeDto[];
    createIntakeOnDischarge: PatientIntakeDto;
    pccPatientProgressNoteDetailsLoading: boolean;
    pccPatientProgressNoteDetails: PccProgressNoteDetailAggregationDto;
    returnToHospitals: ReturnToHospitalSummaryDto[];
    ccdaDocuments: CcdaDocumentListItemDto[];
    providersAndSuppliers: PatientProviderSupplierDto[];
    awvPreventativeItem: AwvPreventativeDto;
    patientHospiceIntakes: HospiceIntakeDto[];
    showHospiceIntake: boolean;
    cqmStatusRows: CqmStatusRowDto[] | null;
    patientImmunizations: PatientImmunizationDto[];
    patientImmunizationsLoading: boolean;
    patientImmunizationProcessing: boolean;
    preventativePatientImmunizations: PatientImmunizationDto[];
    preventativePatientImmunizationsLoading: boolean;
    patientMedications: PatientMedicationDto[];
    // SCRUM-1489 - which patient patientMedications belongs to, so a refetch only clears rows that are someone else's.
    patientMedicationsPatientId: number | null;
    // SCRUM-1489 - the last fetch for the tagged patient failed, so the empty list is unknown, not "none".
    patientMedicationsLoadFailed: boolean;
    patientMedicationsLoading: boolean;
    patientAllergies: PatientAllergyDto[];
    // SCRUM-1372 - which patient patientAllergies belongs to, so a refetch only clears rows that are someone else's.
    patientAllergiesPatientId: number | null;
    patientAllergiesLoading: boolean;
    patientCareGoals: PatientCareGoalDto[];
    // SCRUM-1117 - which patient patientCareGoals belongs to, so a refetch only clears rows that are someone else's.
    patientCareGoalsPatientId: number | null;
    patientCareGoalsLoading: boolean;
    // SCRUM-1503 - planned goals (entered through Add Order) are their own list, so loading one never replaces the
    // other. Same ownership-tag discipline as patientCareGoals.
    plannedCareGoals: PatientCareGoalDto[];
    plannedCareGoalsPatientId: number | null;
    plannedCareGoalsLoading: boolean;
    patientHealthConcerns: PatientHealthConcernDto[];
    // SCRUM-1414 - which patient patientHealthConcerns belongs to, so a refetch only clears rows that are someone else's.
    patientHealthConcernsPatientId: number | null;
    patientHealthConcernsLoading: boolean;
    patientCareTeamMembers: PatientCareTeamMemberDto[];
    // SCRUM-1215 - which patient patientCareTeamMembers belongs to, so a refetch only clears rows that are someone else's.
    patientCareTeamMembersPatientId: number | null;
    patientCareTeamMembersLoading: boolean;
    immunizationRefusalReasons: ImmunizationRefusalReasonDto[];
    immunizationRefusalReasonsLoaded: boolean;
    immunizationRouteCodes: ImmunizationRouteCodeDto[];
    immunizationRouteCodesLoaded: boolean;
    // SCRUM-1570: the HL7 v2 code tables behind the registry fields (site, unit, funding, VFC, VIS, publicity, status).
    immunizationRegistryCodes: ImmunizationRegistryCodeTablesDto | null;
    immunizationRegistryStaff: RegistryStaffDto[];
    immunizationRegistryStaffLoaded: boolean;
}

export const initialState: CensusState = {
    errorMessage: null,
    badges: censusBadgesAdapter.getInitialState({}),
    visitDues: visitDuesAdapter.getInitialState({}),
    specialties: specialtiesAdapter.getInitialState({}),
    census: [],
    censusGroupsToRefresh: [],
    censusFilters: null,
    currentPatientIntake: null,
    currentPatientIntakeLockStatus: null,
    currentPatientIntakeIsLocked: false,
    currentPatientIntakeLockActive: false,
    currentStagedNote: null,
    currentStagedNotes: [],
    currentStagedNotesGrouped: [],
    completedNotes: null,
    incompleteNotes: null,
    quickNotes: null,
    facilityQuickNotes: null,
    currentNote: null,
    latestHgbA1cLab: null,
    latestTshLab: null,
    pendingMedicalSummaryChanges: false,
    pendingMedicalSummaryFields: null,
    matrixCarePatient: null,
    matrixCarePatientLoading: null,
    pccPatient: null,
    pccPatientLoading: null,
    facilityAdtsLoading: null,
    facilityAdts: null,
    facilityRecentAdmissions: null,
    facilityRecentDischarges: null,
    patientNoteLoading: false,
    patientNoteProcessing: false,
    patientNotes: null,
    currentPatientNote: null,
    patient: null,
    searchResults: null,
    recentSearchSelectedPatientIntakes: null,
    patientSearchLoading: false,
    patientSearchVisible: false,
    patientLabProcessing: false,
    dischargeModalVisible: false,
    overrideDischargeConfirmationVisible: false,
    requiresReload: false,
    noteIsReadonly: false,
    currentCensus: null,
    noteError: null,
    currentNoteType: null,
    patientDrawerOpen: false,
    noteCensus: null,
    notePrintContent: [],
    quickNotePrintContent: [],
    sideNotePrintContent: null,
    cptCodes: [],
    procedureCptCodes: [],
    noteFaxes: [],
    recentLabsLoading: false,
    recentLabs: [],
    recentLabTestDetails: [],
    schedules: [],
    visits: [],
    visitButtons: [],
    patientDocumentGroups: [],
    isQuickNoteVisible: null,
    patientIntakes: [],
    createIntakeOnDischarge: null,
    pccPatientProgressNoteDetails: null,
    pccPatientProgressNoteDetailsLoading: false,
    returnToHospitals: [],
    ccdaDocuments: [],
    providersAndSuppliers: null,
    awvPreventativeItem: null,
    patientHospiceIntakes: [],
    showHospiceIntake: false,
    cqmStatusRows: null,
    patientImmunizations: [],
    patientImmunizationsLoading: false,
    patientImmunizationProcessing: false,
    preventativePatientImmunizations: [],
    preventativePatientImmunizationsLoading: false,
    patientMedications: [],
    patientMedicationsPatientId: null,
    patientMedicationsLoadFailed: false,
    patientMedicationsLoading: false,
    patientAllergies: [],
    patientAllergiesPatientId: null,
    patientAllergiesLoading: false,
    patientCareGoals: [],
    patientCareGoalsPatientId: null,
    patientCareGoalsLoading: false,
    plannedCareGoals: [],
    plannedCareGoalsPatientId: null,
    plannedCareGoalsLoading: false,
    patientHealthConcerns: [],
    patientHealthConcernsPatientId: null,
    patientHealthConcernsLoading: false,
    patientCareTeamMembers: [],
    patientCareTeamMembersPatientId: null,
    patientCareTeamMembersLoading: false,
    immunizationRefusalReasons: [],
    immunizationRefusalReasonsLoaded: false,
    immunizationRouteCodes: [],
    immunizationRouteCodesLoaded: false,
    immunizationRegistryCodes: null,
    immunizationRegistryStaff: [],
    immunizationRegistryStaffLoaded: false,
};
